/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  brokerageCostBasisElections,
  generateCostBasisPayload,
  SPECIFIC_LOT_IDENTIFICATION
} from 'services/CostBasisMethod';
import { DATE_DISPLAY_FORMAT, CURRENT_YEAR, PRIOR_YEAR } from 'services/Dates';
import { taxResourcesTurboTaxLink } from 'services/Externals';
import { formatAccountNumber } from 'services/Formats';
import { extractAndFormatOwners, isHiddenAccount } from 'services/Accounts';
import { SERVICE_TYPE_HNW, SERVICE_TYPE_MIL } from 'services/ServiceBenefits';
import {
  isViewOnlyAccount,
  isTaxesEligible,
  productMap,
  isActiveAdvisoryOrActivePlus
} from 'services/Products';
import { filterPendingTransactionsByAccount } from 'services/Transactions';
import { getCustomerServiceType } from 'state/customer/CustomerSelectors';
import {
  getAccounts,
  getAccountDetailsId,
  getAccountsById,
  getPriorBusinessDay,
  getAsOfDate,
  getBalances
} from 'state/account/AccountSelectors';
import { getPendingTransactionsTable } from 'state/transaction/TransactionSelectors';
import { filterFunctionMapping } from 'state/shared/FilterFunctionMappingUtil';
import { isRMDEligibleAccount } from 'services/AccountTypes';
import {
  getSmallBusinessRetirementAgreements,
  getTaxonomyDataV2
} from 'state/taxonomy/TaxonomySelectors';
import {ytdTableColumns} from "components/shared/Text/ColumnHeaders";
import isNil from "lodash/isNil";

export const getCostBasisBalances = (state) => state.costBasisBalances;
export const getCostBasisElections = (state) => state.costBasisElections;
const getPriorYearIndicator = (state) => state.priorYearIndicator;
export const getYtdTaxInformation = (state) => state.ytdTaxInformation;
const getIraContributions = (state) => state.iraContributions;
const getIraDistributions = (state) => state.iraDistributions;
const getIraWithholdings = (state) => state.iraWithholdings;
export const getTaxFilter = (state) => state.ytdTaxFiltering;
const getCollegeSavingsActivities = (state) => state.collegeSavingsActivities;
export const getCollegeSavingsFilter = (state) => state.collegeSavingsFilter;
const getPendingCostBasisElections = (state) => state.pendingCostBasisElections;
export const getURZGainsLossesData = (state) => state.unrealizedGainsLosses?.unrealizedGainsLosses;
export const getRZGainsLossesPriorYear = (state) => state.realizedGainsLossesPrior?.realizedPriorYearData;
export const getRZGainsLossesCurrentYear = (state) => state.realizedGainsLossesCurrent?.realizedCurrentYearData;
export const getIsPrintMode = (state) => state.isPrintMode?.isPrintMode;

export function accountsTotals(accountType, ytdTaxInformation, displayYear) {
  const totals = ytdTableColumns.map(columnName => ({ columnName, total: 0 }));

  accountType?.agreements?.forEach(agreement => {
    const ytdTaxData = ytdTaxInformation?.find(ytd => ytd.accountId === agreement.agreementKey.agreementID);
    totals?.forEach(column => {
      const value = displayYear === CURRENT_YEAR.toString() ? ytdTaxData[`currentYear${column.columnName}`] : ytdTaxData[`priorYear${column.columnName}`];
      column.total += value;
    });
  });
  return totals;
}

export const getTaxesTaxonomyData = createSelector(
    getTaxonomyDataV2,
    getYtdTaxInformation,
    getAccounts,
    getTaxFilter,
    getSmallBusinessRetirementAgreements,
    (taxonomyData, ytdTaxInformation, accountsList, taxFilter, smallBusinessAgreements) => {

      // filter the accounts depending on dropdown selection
      let filteredYtdTaxInformation = ytdTaxInformation;
      if (taxFilter.selection === 'smallBusiness') {
        const accounts = Object.values(accountsList);

        const filteredAccountIds = accounts.filter((account) => {
          return smallBusinessAgreements.some(agreement => (agreement.id === account.accountId));
        }).map(account => account.accountId);

        filteredYtdTaxInformation = filteredYtdTaxInformation.filter((taxInfo) =>
            filteredAccountIds.includes(taxInfo.accountId)
        );
      } else if (taxFilter.selection !== 'all') {
        const accounts = Object.values(accountsList);
        const filteredAccountIds = accounts
            .filter(
                filterFunctionMapping(
                    taxFilter.selection,
                    productMap,
                    taxFilter.accountIds
                )
            )
            .map((account) => account.accountId);
        filteredYtdTaxInformation = filteredYtdTaxInformation.filter((taxInfo) =>
            filteredAccountIds.includes(taxInfo.accountId)
        );
      }

      // loop through taxonomyData to apply filters
      return taxonomyData.map(hierarchy => {
        const filteredAccountTypeGroups = hierarchy.accountGroup.map(accountType => {
          const filteredAgreements = accountType.agreements.filter(agreement => {
            // filter to only get the funds in the ytdTaxInformation response
            return filteredYtdTaxInformation.some(ytd => ytd.accountId === agreement.agreementKey.agreementID);
          });

          // return a new accountType object with filtered agreements
          return {
            ...accountType,
            agreements: filteredAgreements
          };
        });

        // return a new category object with filtered account groups
        return {
          ...hierarchy,
          accountGroup: filteredAccountTypeGroups
        };
      })
          // filter out any account types that have no agreements and any categories that have no account types
          .map(categoryGroup => {
            return {
              ...categoryGroup,
              accountGroup: categoryGroup.accountGroup.filter(accountType =>
                  accountType.agreements.length > 0
              )
            };
          }).filter(hierarchy => hierarchy.accountGroup.length > 0);
    }
);

export const getUnrealizedTableData = createSelector(
  getRZGainsLossesPriorYear,
  getRZGainsLossesCurrentYear,
  getURZGainsLossesData,
  getAccounts,
  (
    rzGainsLossesPrior,
    rzGainsLossesCurrent,
    urzGainsLossesData,
    allAccounts
  ) => {
    if (urzGainsLossesData) {
      return urzGainsLossesData
        .filter((account) => {
          const accountId = account?.accountDescriptor.accountId;
          const curAccount = allAccounts?.find(
            (acc) => acc?.accountId === accountId
          );
          if (curAccount?.isViewOnly) return false;
          return (
            !!account?.unrealizedCostBasisDetails?.gainLossTotals ||
            account?.recordStatus === 'UNKNOWN'  
            
          );
        })
        .map((account) => {
          const { accountDescriptor, unrealizedCostBasisDetails } = account;
          const { accountId, accountNumber } = accountDescriptor;
          const { asOfDate, gainLossTotals, holdingsCostBasis, recordStatus } =
            unrealizedCostBasisDetails;
          const rzGainsLossesPriorYearData = rzGainsLossesPrior
            ? rzGainsLossesPrior.find(
                (rzAccount) =>
                  rzAccount.accountDescriptor.accountId === accountId
              )
            : null;
          const rzGainsLossesCurrentYearData = rzGainsLossesCurrent
            ? rzGainsLossesCurrent.find(
                (rzAccount) =>
                  rzAccount.accountDescriptor.accountId === accountId
              )
            : null;
          const rzRecordStatus =
            rzGainsLossesPriorYearData?.recordStatus === 'UNKNOWN' ||
            rzGainsLossesCurrentYearData?.recordStatus === 'UNKNOWN';
          const urzRecordStatus = recordStatus === 'UNKNOWN';
          const status =
            rzRecordStatus || urzRecordStatus ? 'UNKNOWN' : 'UP_TO_DATE';

          return {
            // account level
            taxesUniqueId: accountId,
            accountNumber,
            asOfDate,
            recordStatus: status,
            accountGainLossAmount: gainLossTotals?.gainLoss.total,
            accountGainLossPercent: gainLossTotals?.gainLossPercent.total,
            accountCostPerShare: gainLossTotals?.unitCost,
            accountCostBasisAmount: gainLossTotals?.currentCost.total,
            accountMarketValue: gainLossTotals?.marketValue,
            accountShares: gainLossTotals?.priorDayQuantity,
            shortTermGainLossAmount: gainLossTotals?.gainLoss.shortTerm,
            shortTermGainLossPercent: gainLossTotals?.gainLossPercent.shortTerm,
            longTermGainLossAmount: gainLossTotals?.gainLoss.longTerm,
            longTermGainLossPercent: gainLossTotals?.gainLossPercent.longTerm,
            missingCostBasis: gainLossTotals?.missingCostBasis,
            // holdings level
            holdings: holdingsCostBasis
              ?.map((holding) => {
                const {
                  cusip,
                  securityDesc,
                  ticker,
                  gainLossTotals,
                  coveredGainLossTotals,
                  nonCoveredGainLossTotals,
                  shortGainLossTotals,
                  recordStatus
                } = holding;
                return {
                  taxesUniqueId: accountId + cusip,
                  cusip,
                  accountName: securityDesc,
                  ticker: ticker,
                  recordStatus,
                  missingCostBasis: gainLossTotals.missingCostBasis,
                  accountGainLossAmount: gainLossTotals.gainLoss.total,
                  accountGainLossPercent: gainLossTotals.gainLossPercent.total,
                  accountCostPerShare: gainLossTotals.unitCost,
                  accountCostBasisAmount: gainLossTotals.currentCost.total,
                  accountMarketValue: gainLossTotals.marketValue,
                  accountShares: gainLossTotals.priorDayQuantity,
                  shortTermGainLossAmount: gainLossTotals.gainLoss.shortTerm,
                  shortTermGainLossPercent:
                    gainLossTotals.gainLossPercent.shortTerm,
                  longTermGainLossAmount: gainLossTotals.gainLoss.longTerm,
                  longTermGainLossPercent:
                    gainLossTotals.gainLossPercent.longTerm,
                  // holdings - covered
                  covered: coveredGainLossTotals
                    ? {
                        covered: 'Covered',
                        missingCostBasis:
                          coveredGainLossTotals.missingCostBasis,
                        gainLossAmount: coveredGainLossTotals.gainLoss.total,
                        gainLossPercent:
                          coveredGainLossTotals.gainLossPercent.total,
                        costPerShare: coveredGainLossTotals.unitCost,
                        costBasisAmount:
                          coveredGainLossTotals.currentCost.total,
                        marketValue: coveredGainLossTotals.marketValue,
                        shares: coveredGainLossTotals.priorDayQuantity,
                        shortTermGainLossAmount:
                          coveredGainLossTotals.gainLoss.shortTerm,
                        shortTermGainLossPercent:
                          coveredGainLossTotals.gainLossPercent.shortTerm,
                        longTermGainLossAmount:
                          coveredGainLossTotals.gainLoss.longTerm,
                        longTermGainLossPercent:
                          coveredGainLossTotals.gainLossPercent.longTerm
                      }
                    : null,
                  // holdings - uncovered
                  uncovered: nonCoveredGainLossTotals
                    ? {
                        covered: 'Noncovered',
                        missingCostBasis:
                          nonCoveredGainLossTotals.missingCostBasis,
                        gainLossAmount: nonCoveredGainLossTotals.gainLoss.total,
                        gainLossPercent:
                          nonCoveredGainLossTotals.gainLossPercent.total,
                        costPerShare: nonCoveredGainLossTotals.unitCost,
                        costBasisAmount:
                          nonCoveredGainLossTotals.currentCost.total,
                        marketValue: nonCoveredGainLossTotals.marketValue,
                        shares: nonCoveredGainLossTotals.priorDayQuantity,
                        shortTermGainLossAmount:
                          nonCoveredGainLossTotals.gainLoss.shortTerm,
                        shortTermGainLossPercent:
                          nonCoveredGainLossTotals.gainLossPercent.shortTerm,
                        longTermGainLossAmount:
                          nonCoveredGainLossTotals.gainLoss.longTerm,
                        longTermGainLossPercent:
                          nonCoveredGainLossTotals.gainLossPercent.longTerm
                      }
                    : null,
                  // holdings - short
                  short: shortGainLossTotals
                    ? {
                        covered: 'Short',
                        missingCostBasis: shortGainLossTotals.missingCostBasis,
                        gainLossAmount: shortGainLossTotals.gainLoss.total,
                        gainLossPercent:
                          shortGainLossTotals.gainLossPercent.total,
                        costPerShare: shortGainLossTotals.unitCost,
                        costBasisAmount: shortGainLossTotals.currentCost.total,
                        marketValue: shortGainLossTotals.marketValue,
                        shares: shortGainLossTotals.priorDayQuantity,
                        shortTermGainLossAmount:
                          shortGainLossTotals.gainLoss.shortTerm,
                        shortTermGainLossPercent:
                          shortGainLossTotals.gainLossPercent.shortTerm,
                        longTermGainLossAmount:
                          shortGainLossTotals.gainLoss.longTerm,
                        longTermGainLossPercent:
                          shortGainLossTotals.gainLossPercent.longTerm
                      }
                    : null
                };
              })
              .sort((a, b) => {
                const nameA = a.accountName.replace(/\./g, '').toUpperCase();
                const nameB = b.accountName.replace(/\./g, '').toUpperCase();
                return nameA.localeCompare(nameB);
              })
          };
        });
    } else {
      return [];
    }
  }
);

// This maps the response for prior and current year for realized data
export const mapRealizedYearData = (
  urzGainsLossesData,
  gainsLosses,
  isCurrentYear,
  allAccounts = []
) => {
  return gainsLosses
    ?.filter((account) => {
      const accountId = account?.accountDescriptor.accountId;
      const curAccount = allAccounts?.find(acc=>acc.accountId === accountId);
      if (curAccount?.isViewOnly)return false;

        return isCurrentYear
          ? !!account?.realizedCurrentYearCostBasisDetails?.gainLossTotals ||
              account?.recordStatus === 'UNKNOWN'
          : !!account?.realizedPriorYearCostBasisDetails?.gainLossTotals ||
              account?.recordStatus === 'UNKNOWN';
    })
    .map((account) => {
      const realizedCostBasisDetails = isCurrentYear
        ? account.realizedCurrentYearCostBasisDetails
        : account.realizedPriorYearCostBasisDetails;
      const { accountDescriptor, recordStatus } = account;
      const { accountId, accountNumber } = accountDescriptor;
      const { gainLossTotals, holdingsCostBasis } = realizedCostBasisDetails;
      const rzRecordStatus = recordStatus === 'UNKNOWN';
      const unrealizedCostBasisDetails = urzGainsLossesData?.find(
        (urzAccount) => urzAccount.accountDescriptor.accountId === accountId
      );
      const urzRecordStatus =
        unrealizedCostBasisDetails?.recordStatus === 'UNKNOWN';
      const status =
        rzRecordStatus || urzRecordStatus ? 'UNKNOWN' : 'UP_TO_DATE';

      return {
        // account level
        taxesUniqueId: accountId,
        accountNumber,
        recordStatus: status,
        shortTermGainLossAmount: gainLossTotals?.gainLoss.shortTerm,
        shortTermGainLossPercent: gainLossTotals?.gainLossPercent.shortTerm,
        longTermGainLossAmount: gainLossTotals?.gainLoss.longTerm,
        longTermGainLossPercent: gainLossTotals?.gainLossPercent.longTerm,
        totalGainLossAmount: gainLossTotals?.gainLoss.total,
        totalGainLossPercent: gainLossTotals?.gainLossPercent.total,
        proceeds: gainLossTotals?.netProceed,
        costBasisAmount: gainLossTotals?.currentCost.total,
        missingCostBasis: gainLossTotals?.missingCostBasis,
        // holdings level
        holdings: holdingsCostBasis?.map((holding) => {
          const {
            cusip,
            securityDesc,
            ticker,
            gainLossTotals,
            coveredGainLossTotals,
            nonCoveredGainLossTotals,
            shortGainLossTotals,
            recordStatus
          } = holding;
          return {
            taxesUniqueId: accountId + cusip,
            cusip,
            accountName: securityDesc,
            ticker,
            recordStatus,
            shortTermGainLossAmount: gainLossTotals.gainLoss.shortTerm,
            shortTermGainLossPercent: gainLossTotals.gainLossPercent.shortTerm,
            longTermGainLossAmount: gainLossTotals.gainLoss.longTerm,
            longTermGainLossPercent: gainLossTotals.gainLossPercent.longTerm,
            totalGainLossAmount: gainLossTotals.gainLoss.total,
            totalGainLossPercent: gainLossTotals.gainLossPercent.total,
            proceeds: gainLossTotals.netProceed,
            costBasisAmount: gainLossTotals.currentCost.total,
            missingCostBasis: gainLossTotals.missingCostBasis,
            // holdings - covered
            covered: coveredGainLossTotals
              ? {
                  covered: 'Covered',
                  shortTermGainLossAmount:
                    coveredGainLossTotals.gainLoss.shortTerm,
                  shortTermGainLossPercent:
                    coveredGainLossTotals.gainLossPercent.shortTerm,
                  longTermGainLossAmount:
                    coveredGainLossTotals.gainLoss.longTerm,
                  longTermGainLossPercent:
                    coveredGainLossTotals.gainLossPercent.longTerm,
                  totalGainLossAmount: coveredGainLossTotals.gainLoss.total,
                  totalGainLossPercent:
                    coveredGainLossTotals.gainLossPercent.total,
                  proceeds: coveredGainLossTotals.netProceed,
                  costBasisAmount: coveredGainLossTotals.currentCost.total,
                  missingCostBasis: coveredGainLossTotals.missingCostBasis
                }
              : null,
            // holdings - uncovered
            uncovered: nonCoveredGainLossTotals
              ? {
                  covered: 'Noncovered',
                  shortTermGainLossAmount:
                    nonCoveredGainLossTotals.gainLoss.shortTerm,
                  shortTermGainLossPercent:
                    nonCoveredGainLossTotals.gainLossPercent.shortTerm,
                  longTermGainLossAmount:
                    nonCoveredGainLossTotals.gainLoss.longTerm,
                  longTermGainLossPercent:
                    nonCoveredGainLossTotals.gainLossPercent.longTerm,
                  totalGainLossAmount: nonCoveredGainLossTotals.gainLoss.total,
                  totalGainLossPercent:
                    nonCoveredGainLossTotals.gainLossPercent.total,
                  proceeds: nonCoveredGainLossTotals.netProceed,
                  costBasisAmount: nonCoveredGainLossTotals.currentCost.total,
                  missingCostBasis: nonCoveredGainLossTotals.missingCostBasis
                }
              : null,
            short: shortGainLossTotals
              ? {
                  covered: 'Short',
                  shortTermGainLossAmount:
                    shortGainLossTotals.gainLoss.shortTerm,
                  shortTermGainLossPercent:
                    shortGainLossTotals.gainLossPercent.shortTerm,
                  longTermGainLossAmount: shortGainLossTotals.gainLoss.longTerm,
                  longTermGainLossPercent:
                    shortGainLossTotals.gainLossPercent.longTerm,
                  totalGainLossAmount: shortGainLossTotals.gainLoss.total,
                  totalGainLossPercent:
                    shortGainLossTotals.gainLossPercent.total,
                  proceeds: shortGainLossTotals.netProceed,
                  costBasisAmount: shortGainLossTotals.currentCost.total,
                  missingCostBasis: shortGainLossTotals.missingCostBasis
                }
              : null
          };
        })
      };
    });
};

export const getRealizedTableData = createSelector(
    getRZGainsLossesPriorYear,
    getRZGainsLossesCurrentYear,
    getURZGainsLossesData,
    getAccounts,
    (rzGainsLossesPrior, rzGainsLossesCurrent, urzGainsLossesData,allAccounts) => {

      const priorYearBalances = rzGainsLossesPrior
        ? mapRealizedYearData(
            urzGainsLossesData,
            rzGainsLossesPrior,
            false,
            allAccounts
          )
        : [];
      const currentYearBalances = rzGainsLossesCurrent
        ? mapRealizedYearData(
            urzGainsLossesData,
            rzGainsLossesCurrent,
            true,
            allAccounts
          )
        : [];

      return {
        [PRIOR_YEAR]: priorYearBalances,
        [CURRENT_YEAR]: currentYearBalances
      };
    }
);

export const getDefaultTaxYear = createSelector(
  getPriorYearIndicator,
  (priorYearIndicator) => {
    return priorYearIndicator ? PRIOR_YEAR.toString() : CURRENT_YEAR.toString();
  }
);

export const getCurrentAndPriorYearBalances = createSelector(
  getCostBasisBalances,
  (costBasisBalances) => {
    const currentYearBalances = costBasisBalances.filter(
      (balance) => balance.taxYear === CURRENT_YEAR
    );
    const priorYearBalances = costBasisBalances.filter(
      (balance) => balance.taxYear === PRIOR_YEAR
    );

    return {
      [CURRENT_YEAR]: currentYearBalances,
      [PRIOR_YEAR]: priorYearBalances
    };
  }
);

function createCostBasisTableData(
  accountsById,
  costBasisBalances,
  costBasisElections
) {
  const unrealizedCostBasisGroups = {};
  const realizedCostBasisGroups = {};
  costBasisBalances.forEach((balance) => {
    const { accountId, asOfDate } = balance;
    const account = accountsById[accountId];
    if (account) {
      const { accountName, accountType, ticker, productType } = account;
      const accountWChecksum = formatAccountNumber(account);
      const owners = extractAndFormatOwners(account);
      const { costBasisMethod } =
      costBasisElections.find(
        (election) => election.accountDescriptor.accountId === accountId
      ) || '';
      const unrealizedGroup = unrealizedCostBasisGroups[accountType] || [];
      const realizedGroup = realizedCostBasisGroups[accountType] || [];

      const unrealizedPositions = [];
      balance.costBasisPositions &&
      balance.costBasisPositions.forEach((position) => {
        if (
          position.unrealizedTotalShares &&
          position.unrealizedTotalShares > 0
        ) {
          unrealizedPositions.push({
            costBasisMethod,
            investmentVehicle: position.investmentVehicle,
            covered: {
              shares: position.coveredAvailableShareQuantity,
              marketValue: position.coveredMarketValue,
              costPerShare: position.coveredCostPerShare,
              costBasisAmount: position.coveredCostBasisAmount,
              gainLossAmount: position.coveredTotalUnrealizedGainLoss,
              gainLossPercent: position.coveredTotalUnrealizedGainLossPercent
            },
            noncovered: {
              shares: position.nonCoveredAvailableShareQuantity,
              marketValue: position.nonCoveredMarketValue,
              costPerShare: position.nonCoveredCostPerShare,
              costBasisAmount: position.nonCoveredCostBasisAmount,
              gainLossAmount: position.nonCoveredTotalUnrealizedGainLoss,
              gainLossPercent:
              position.nonCoveredTotalUnrealizedGainLossPercent
            },
            total: {
              shares: position.unrealizedTotalShares,
              marketValue: position.unrealizedTotalMarketValue,
              costPerShare: position.unrealizedTotalCostPerShare,
              costBasisAmount: position.unrealizedTotalCostBasisAmount,
              gainLossAmount: position.unrealizedTotalGainLoss,
              gainLossPercent: position.unrealizedTotalGainLossPercent
            }
          });
        }
      });
      const realizedPositions = [];
      balance.costBasisPositions &&
      balance.costBasisPositions.forEach((position) => {
        if (position.totalProceeds && position.totalProceeds !== 0) {
          realizedPositions.push({
            costBasisMethod,
            investmentVehicle: position.investmentVehicle,
            covered: {
              shares: position.coveredAvailableShareQuantity,
              proceeds: position.coveredProceeds,
              costBasisAmount: position.realizedCoveredCostBasisAmount,
              shortTermGainLossAmount:
              position.coveredRealizedShortTermGainLoss,
              shortTermGainLossPercent:
              position.coveredRealizedShortTermGainLossPercent,
              longTermGainLossAmount:
              position.coveredRealizedLongTermGainLoss,
              longTermGainLossPercent:
              position.coveredRealizedLongTermGainLossPercent,
              totalGainLossAmount:
                position.coveredRealizedShortTermGainLoss +
                position.coveredRealizedLongTermGainLoss,
              totalGainLossPercent:
              position.coveredRealizedTotalGainLossPercent
            },
            noncovered: {
              shares: position.nonCoveredAvailableShareQuantity,
              proceeds: position.nonCoveredProceeds,
              costBasisAmount: position.realizedNonCoveredCostBasisAmount,
              shortTermGainLossAmount:
              position.nonCoveredRealizedShortTermGainLoss,
              shortTermGainLossPercent:
              position.nonCoveredRealizedShortTermGainLossPercent,
              longTermGainLossAmount:
              position.nonCoveredRealizedLongTermGainLoss,
              longTermGainLossPercent:
              position.nonCoveredRealizedLongTermGainLossPercent,
              totalGainLossAmount:
                position.nonCoveredRealizedShortTermGainLoss +
                position.nonCoveredRealizedLongTermGainLoss,
              totalGainLossPercent:
              position.nonCoveredRealizedTotalGainLossPercent
            },
            total: {
              proceeds: position.totalProceeds,
              costBasisAmount: position.realizedTotalCostBasisAmount,
              shortTermGainLossAmount:
              position.realizedTotalShortTermGainLoss,
              shortTermGainLossPercent:
              position.realizedTotalShortTermGainLossPercent,
              longTermGainLossAmount: position.realizedTotalLongTermGainLoss,
              longTermGainLossPercent:
              position.realizedTotalLongTermGainLossPercent,
              totalGainLossAmount: position.realizedTotalGainLoss,
              totalGainLossPercent: position.realizedTotalGainLossPercent
            }
          });
        }
      });

      if (balance.unrealizedTotalShares && balance.unrealizedTotalShares > 0) {
        unrealizedGroup.push({
          account: {
            accountName,
            taxesUniqueId: accountId,
            accountWChecksum,
            owners,
            ticker,
            asOfDate,
            productType
          },
          balance: {
            costBasisMethod,
            covered: {
              shares: balance.coveredAvailableShareQuantity,
              marketValue: balance.coveredMarketValue,
              costPerShare: balance.coveredCostPerShare,
              costBasisAmount: balance.coveredCostBasisAmount,
              gainLossAmount: balance.coveredTotalUnrealizedGainLoss,
              gainLossPercent: balance.coveredTotalUnrealizedGainLossPercent
            },
            noncovered: {
              shares: balance.nonCoveredAvailableShareQuantity,
              marketValue: balance.nonCoveredMarketValue,
              costPerShare: balance.nonCoveredCostPerShare,
              costBasisAmount: balance.nonCoveredCostBasisAmount,
              gainLossAmount: balance.nonCoveredTotalUnrealizedGainLoss,
              gainLossPercent: balance.nonCoveredTotalUnrealizedGainLossPercent
            },
            total: {
              shares: balance.unrealizedTotalShares,
              marketValue: balance.unrealizedTotalMarketValue,
              costPerShare: balance.unrealizedTotalCostPerShare,
              costBasisAmount: balance.unrealizedTotalCostBasisAmount,
              gainLossAmount: balance.unrealizedTotalGainLoss,
              gainLossPercent: balance.unrealizedTotalGainLossPercent
            },
            positions: unrealizedPositions
          }
        });
      }

      unrealizedCostBasisGroups[accountType] = unrealizedGroup;

      const realizedData = {
        account: {
          accountName,
          taxesUniqueId: accountId,
          accountWChecksum,
          owners,
          ticker,
          asOfDate
        },
        balance: {
          covered: {
            shares: balance.coveredAvailableShareQuantity,
            proceeds: balance.coveredProceeds,
            costBasisAmount: balance.realizedCoveredCostBasisAmount,
            shortTermGainLossAmount: balance.coveredRealizedShortTermGainLoss,
            shortTermGainLossPercent:
            balance.coveredRealizedShortTermGainLossPercent,
            longTermGainLossAmount: balance.coveredRealizedLongTermGainLoss,
            longTermGainLossPercent:
            balance.coveredRealizedLongTermGainLossPercent,
            totalGainLossAmount:
              balance.coveredRealizedShortTermGainLoss +
              balance.coveredRealizedLongTermGainLoss,
            totalGainLossPercent: balance.coveredRealizedTotalGainLossPercent
          },
          noncovered: {
            shares: balance.nonCoveredAvailableShareQuantity,
            proceeds: balance.nonCoveredProceeds,
            costBasisAmount: balance.realizedNonCoveredCostBasisAmount,
            shortTermGainLossAmount:
            balance.nonCoveredRealizedShortTermGainLoss,
            shortTermGainLossPercent:
            balance.nonCoveredRealizedShortTermGainLossPercent,
            longTermGainLossAmount: balance.nonCoveredRealizedLongTermGainLoss,
            longTermGainLossPercent:
            balance.nonCoveredRealizedLongTermGainLossPercent,
            totalGainLossAmount:
              balance.nonCoveredRealizedShortTermGainLoss +
              balance.nonCoveredRealizedLongTermGainLoss,
            totalGainLossPercent: balance.nonCoveredRealizedTotalGainLossPercent
          },
          total: {
            proceeds: balance.totalProceeds,
            costBasisAmount: balance.realizedTotalCostBasisAmount,
            shortTermGainLossAmount: balance.realizedTotalShortTermGainLoss,
            shortTermGainLossPercent:
            balance.realizedTotalShortTermGainLossPercent,
            longTermGainLossAmount: balance.realizedTotalLongTermGainLoss,
            longTermGainLossPercent:
            balance.realizedTotalLongTermGainLossPercent,
            totalGainLossAmount: balance.realizedTotalGainLoss,
            totalGainLossPercent: balance.realizedTotalGainLossPercent
          },
          positions: realizedPositions
        }
      };

      const {
        balance: { covered, noncovered, total }
      } = realizedData;
      const emptyCovered = Object.values(covered).every((value) => value === 0);
      const emptyNoncovered = Object.values(noncovered).every(
        (value) => value === 0
      );
      const emptyTotal = Object.values(total).every((value) => value === 0);
      const allEmpty = emptyCovered && emptyNoncovered && emptyTotal;
      if (!allEmpty) {
        realizedGroup.push(realizedData);
        realizedCostBasisGroups[accountType] = realizedGroup;
      }
    }
  });

  let realizedAsOfDate;
  let unrealizedAsOfDate;

  Object.values(realizedCostBasisGroups).forEach((group) => {
    group.forEach((groupItem) => {
      const {
        account: { asOfDate }
      } = groupItem;
      if (realizedAsOfDate) {
        realizedAsOfDate = moment.utc(asOfDate).isAfter(realizedAsOfDate)
          ? asOfDate
          : realizedAsOfDate;
      } else {
        realizedAsOfDate = asOfDate;
      }
    });
  });

  Object.values(unrealizedCostBasisGroups).forEach((group) => {
    group.forEach((groupItem) => {
      const {
        account: { asOfDate }
      } = groupItem;
      if (unrealizedAsOfDate) {
        unrealizedAsOfDate = moment.utc(asOfDate).isAfter(unrealizedAsOfDate)
          ? asOfDate
          : unrealizedAsOfDate;
      } else {
        unrealizedAsOfDate = asOfDate;
      }
    });
  });

  const unrealizedCostBasisRows = Object.entries(unrealizedCostBasisGroups)
    .map(([accountType, rows]) => ({
      accountType,
      rows: rows.sort((a, b) =>
        a.account.accountName.localeCompare(b.account.accountName)
      )
    }))
    .sort((a, b) => a.accountType.localeCompare(b.accountType));

  const realizedCostBasisRows = Object.entries(realizedCostBasisGroups)
    .map(([accountType, rows]) => ({
      accountType,
      rows: rows.sort((a, b) =>
        a.account.accountName.localeCompare(b.account.accountName)
      )
    }))
    .sort((a, b) => a.accountType.localeCompare(b.accountType));

  return {
    realizedAsOfDate: moment.utc(realizedAsOfDate).format(DATE_DISPLAY_FORMAT),
    realizedCostBasisRows,
    unrealizedAsOfDate: moment
      .utc(unrealizedAsOfDate)
      .format(DATE_DISPLAY_FORMAT),
    unrealizedCostBasisRows
  };
}

function adjustCostBasisBalances(balances) {
  const formatBalances = (balances, isPosition) => {
    return balances.map((balance) => {
      if (isPosition) {
        if (
          balance.coveredCostBasisAmount === 0 &&
          balance.coveredAvailableShareQuantity !== 0
        ) {
          balance.coveredTotalUnrealizedGainLossPercent = '-';
          balance.coveredTotalUnrealizedGainLoss = '-';
          balance.coveredCostBasisAmount = '-';
          balance.coveredCostPerShare = '-';
          balance.unrealizedTotalGainLossPercent = '-';
          balance.unrealizedTotalCostBasisAmount = '-';
          balance.unrealizedTotalCostPerShare = '-';
        }
        if (
          balance.nonCoveredCostBasisAmount === 0 &&
          balance.nonCoveredAvailableShareQuantity !== 0
        ) {
          balance.nonCoveredTotalUnrealizedGainLossPercent = '-';
          balance.nonCoveredTotalUnrealizedGainLoss = '-';
          balance.nonCoveredCostBasisAmount = '-';
          balance.nonCoveredCostPerShare = '-';
          balance.unrealizedTotalGainLossPercent = '-';
          balance.unrealizedTotalCostBasisAmount = '-';
          balance.unrealizedTotalCostPerShare = '-';
        }
      } else {
        if (
          balance.coveredCostBasisAmount === 0 &&
          balance.coveredMarketValue !== 0
        ) {
          balance.coveredTotalUnrealizedGainLossPercent = '-';
          balance.coveredTotalUnrealizedGainLoss = '-';
          balance.coveredCostBasisAmount = '-';
          balance.coveredCostPerShare = '-';
          balance.unrealizedTotalGainLossPercent = '-';
          balance.unrealizedTotalCostBasisAmount = '-';
          balance.unrealizedTotalCostPerShare = '-';
        }
        if (
          balance.nonCoveredCostBasisAmount === 0 &&
          balance.nonCoveredMarketValue !== 0
        ) {
          balance.nonCoveredTotalUnrealizedGainLossPercent = '-';
          balance.nonCoveredTotalUnrealizedGainLoss = '-';
          balance.nonCoveredCostBasisAmount = '-';
          balance.nonCoveredCostPerShare = '-';
          balance.unrealizedTotalGainLossPercent = '-';
          balance.unrealizedTotalCostBasisAmount = '-';
          balance.unrealizedTotalCostPerShare = '-';
        }
      }

      if (
        balance.realizedCoveredCostBasisAmount === 0 &&
        balance.coveredProceeds !== 0
      ) {
        balance.coveredRealizedTotalGainLossPercent = '-';
        balance.realizedCoveredCostBasisAmount = '-';
        balance.coveredRealizedLongTermGainLossPercent = '-';
        balance.coveredRealizedShortTermGainLossPercent = '-';
        balance.realizedTotalGainLossPercent = '-';
        balance.realizedTotalCostBasisAmount = '-';
        balance.realizedTotalShortTermGainLossPercent = '-';
        balance.realizedTotalLongTermGainLossPercent = '-';
      }
      if (
        balance.realizedNonCoveredCostBasisAmount === 0 &&
        balance.nonCoveredProceeds !== 0
      ) {
        balance.nonCoveredRealizedTotalGainLossPercent = '-';
        balance.realizedNonCoveredCostBasisAmount = '-';
        balance.nonCoveredRealizedShortTermGainLossPercent = '-';
        balance.nonCoveredRealizedLongTermGainLossPercent = '-';
        balance.realizedTotalGainLossPercent = '-';
        balance.realizedTotalCostBasisAmount = '-';
        balance.realizedTotalShortTermGainLossPercent = '-';
        balance.realizedTotalLongTermGainLossPercent = '-';
      }
      return balance;
    });
  };

  const formattedBalances = formatBalances(balances).map((balance) => {
    if (balance.costBasisPositions && balance.costBasisPositions.length > 0) {
      return {
        ...balance,
        costBasisPositions: formatBalances(balance.costBasisPositions, true)
      };
    }
    return balance;
  });

  return formattedBalances;
}

export const getCostBasisTableData = createSelector(
  getAccountsById,
  getCurrentAndPriorYearBalances,
  getCostBasisElections,
  (accountsById, currentAndPriorYearBalances, costBasisElections) => {
    const currentYearBalances = currentAndPriorYearBalances[CURRENT_YEAR];
    const priorYearBalances = currentAndPriorYearBalances[PRIOR_YEAR];
    const adjustedCurrentYearBalances =
      adjustCostBasisBalances(currentYearBalances);
    const adjustedPriorYearBalances =
      adjustCostBasisBalances(priorYearBalances);

    const currentYearCostBasisRows = createCostBasisTableData(
      accountsById,
      adjustedCurrentYearBalances,
      costBasisElections
    );
    const priorYearCostBasisRows = createCostBasisTableData(
      accountsById,
      adjustedPriorYearBalances,
      costBasisElections
    );

    return {
      [CURRENT_YEAR]: currentYearCostBasisRows,
      [PRIOR_YEAR]: priorYearCostBasisRows
    };
  }
);

export const getTotalRealizedGainLoss = createSelector(
  getAccountsById,
  getCurrentAndPriorYearBalances,
  (accountsById, currentAndPriorYearBalances) => {
    const currentYearBalances = currentAndPriorYearBalances[CURRENT_YEAR];
    const priorYearBalances = currentAndPriorYearBalances[PRIOR_YEAR];

    const currentYearGainLoss = {
      longTerm: 0,
      shortTerm: 0,
      total: 0
    };

    const priorYearGainLoss = {
      longTerm: 0,
      shortTerm: 0,
      total: 0
    };

    currentYearBalances.forEach((account) => {
      const {
        realizedTotalGainLoss,
        realizedTotalLongTermGainLoss,
        realizedTotalShortTermGainLoss,
        accountId
      } = account;
      const accountInAccounts = accountsById[accountId];
      if (
        isTaxesEligible(accountInAccounts)
      ) {
        currentYearGainLoss.longTerm += realizedTotalLongTermGainLoss;
        currentYearGainLoss.shortTerm += realizedTotalShortTermGainLoss;
        currentYearGainLoss.total += realizedTotalGainLoss;
      }
    });

    priorYearBalances.forEach((account) => {
      const {
        realizedTotalGainLoss,
        realizedTotalLongTermGainLoss,
        realizedTotalShortTermGainLoss,
        accountId
      } = account;
      const accountInAccounts = accountsById[accountId];
      if (
        isTaxesEligible(accountInAccounts)
      ) {
        priorYearGainLoss.longTerm += realizedTotalLongTermGainLoss;
        priorYearGainLoss.shortTerm += realizedTotalShortTermGainLoss;
        priorYearGainLoss.total += realizedTotalGainLoss;
      }
    });

    return {
      currentYearGainLoss,
      priorYearGainLoss
    };
  }
);

export const addRealizedToGivenYearGainLoss = (gainLoss, account, accountsById) => {
    const {
      realizedTotalGainLoss,
      realizedTotalLongTermGainLoss,
      realizedTotalShortTermGainLoss,
      accountId
    } = account;
    const accountInAccounts = accountsById[accountId];
    if (
        isTaxesEligible(accountInAccounts) &&
        !isActiveAdvisoryOrActivePlus(accountInAccounts?.productType)
    ) {
      gainLoss.longTerm += realizedTotalLongTermGainLoss;
      gainLoss.shortTerm += realizedTotalShortTermGainLoss;
      gainLoss.total += realizedTotalGainLoss;
    }
};

export const addPershingToGivenYearGainLoss = (gainLoss, realizedGivenYearCostBasisDetails, accountDescriptor, accountsById) => {
    const accountInAccounts = accountsById[accountDescriptor.accountId];
    const currentYearCostBasisDetails = realizedGivenYearCostBasisDetails?.gainLossTotals?.gainLoss;
    if (
        currentYearCostBasisDetails &&
        !isHiddenAccount(accountInAccounts) &&
        isTaxesEligible(accountInAccounts)
    ) {
      gainLoss.longTerm += currentYearCostBasisDetails.longTerm;
      gainLoss.shortTerm += currentYearCostBasisDetails.shortTerm;
      gainLoss.total += currentYearCostBasisDetails.total;
    }
};

export const getTotalRealizedGainLossV2 = createSelector(
    getAccountsById,
    getCurrentAndPriorYearBalances,
    getRZGainsLossesCurrentYear,
    getRZGainsLossesPriorYear,
    (accountsById, currentAndPriorYearBalances, pershingCurrentYearBalance, pershingPriorYearBalance) => {
      const currentYearBalances = currentAndPriorYearBalances[CURRENT_YEAR];
      const priorYearBalances = currentAndPriorYearBalances[PRIOR_YEAR];

      const currentYearGainLoss = {
        longTerm: 0,
        shortTerm: 0,
        total: 0
      };

      const priorYearGainLoss = {
        longTerm: 0,
        shortTerm: 0,
        total: 0
      };

      currentYearBalances?.forEach((account) => {
        addRealizedToGivenYearGainLoss(currentYearGainLoss, account, accountsById);
      });

      priorYearBalances?.forEach((account) => {
        addRealizedToGivenYearGainLoss(priorYearGainLoss, account, accountsById);
      });

      pershingCurrentYearBalance?.forEach((account) => {
        const {
          accountDescriptor,
          realizedCurrentYearCostBasisDetails
        } = account;
        addPershingToGivenYearGainLoss(currentYearGainLoss, realizedCurrentYearCostBasisDetails, accountDescriptor, accountsById);
      });

      pershingPriorYearBalance?.forEach((account) => {
        const {
          accountDescriptor,
          realizedPriorYearCostBasisDetails
        } = account;
        addPershingToGivenYearGainLoss(priorYearGainLoss, realizedPriorYearCostBasisDetails, accountDescriptor, accountsById);
      });

      return {
        currentYearGainLoss,
        priorYearGainLoss
      };
    }
);

export const getTaxSummary = createSelector(
  getAccountsById,
  getYtdTaxInformation,
  getTotalRealizedGainLoss,
  (
    accountsById,
    ytdTaxInformation,
    { currentYearGainLoss, priorYearGainLoss }
  ) => {
    const currentYear = {
      capitalGainDistributionLongTerm: 0,
      capitalGainDistributionShortTerm: 0,
      capitalGainDistributionTotal: 0,
      incomeDividend: 0,
      realizedGainLossLongTerm: currentYearGainLoss.longTerm,
      realizedGainLossShortTerm: currentYearGainLoss.shortTerm,
      realizedGainLossTotal: currentYearGainLoss.total
    };
    const priorYear = {
      capitalGainDistributionLongTerm: 0,
      capitalGainDistributionShortTerm: 0,
      capitalGainDistributionTotal: 0,
      incomeDividend: 0,
      realizedGainLossLongTerm: priorYearGainLoss.longTerm,
      realizedGainLossShortTerm: priorYearGainLoss.shortTerm,
      realizedGainLossTotal: priorYearGainLoss.total
    };

    ytdTaxInformation.forEach((account) => {
      const {
        currentYearIncomeDividends,
        currentYearLongTermCapitalGains,
        currentYearShortTermCapitalGains,
        priorYearIncomeDividends,
        priorYearLongTermCapitalGains,
        priorYearShortTermCapitalGains,
        accountId
      } = account;

      const accountInAccounts = accountsById[accountId];
      if (
        isTaxesEligible(accountInAccounts)
      ) {
        currentYear.capitalGainDistributionLongTerm +=
          currentYearLongTermCapitalGains;
        currentYear.capitalGainDistributionShortTerm +=
          currentYearShortTermCapitalGains;
        currentYear.capitalGainDistributionTotal +=
          currentYearLongTermCapitalGains + currentYearShortTermCapitalGains;
        currentYear.incomeDividend += currentYearIncomeDividends;

        priorYear.capitalGainDistributionLongTerm +=
          priorYearLongTermCapitalGains;
        priorYear.capitalGainDistributionShortTerm +=
          priorYearShortTermCapitalGains;
        priorYear.capitalGainDistributionTotal +=
          priorYearLongTermCapitalGains + priorYearShortTermCapitalGains;
        priorYear.incomeDividend += priorYearIncomeDividends;
      }
    });

    return {
      [CURRENT_YEAR]: currentYear,
      [PRIOR_YEAR]: priorYear
    };
  }
);

export const getTaxSummaryV2 = createSelector(
    getAccountsById,
    getYtdTaxInformation,
    getTotalRealizedGainLossV2,
    (
        accountsById,
        ytdTaxInformation,
        { currentYearGainLoss, priorYearGainLoss }
    ) => {
      const currentYear = {
        capitalGainDistributionLongTerm: 0,
        capitalGainDistributionShortTerm: 0,
        capitalGainDistributionTotal: 0,
        incomeDividend: 0,
        // the following are set to total realized gains loss values because a value is always returned
        realizedGainLossLongTerm: currentYearGainLoss.longTerm,
        realizedGainLossShortTerm: currentYearGainLoss.shortTerm,
        realizedGainLossTotal: currentYearGainLoss.total
      };
      const priorYear = {
        capitalGainDistributionLongTerm: 0,
        capitalGainDistributionShortTerm: 0,
        capitalGainDistributionTotal: 0,
        incomeDividend: 0,
        // the following are set to total realized gains loss values because a value is always returned
        realizedGainLossLongTerm: priorYearGainLoss.longTerm,
        realizedGainLossShortTerm: priorYearGainLoss.shortTerm,
        realizedGainLossTotal: priorYearGainLoss.total
      };

      ytdTaxInformation.forEach((account) => {
        const {
          currentYearIncomeDividends,
          currentYearLongTermCapitalGains,
          currentYearShortTermCapitalGains,
          priorYearIncomeDividends,
          priorYearLongTermCapitalGains,
          priorYearShortTermCapitalGains,
          accountId
        } = account;

        const accountInAccounts = accountsById[accountId];
        if (
            isTaxesEligible(accountInAccounts)
        ) {
          currentYear.capitalGainDistributionLongTerm +=
              currentYearLongTermCapitalGains;
          currentYear.capitalGainDistributionShortTerm +=
              currentYearShortTermCapitalGains;
          currentYear.capitalGainDistributionTotal +=
              currentYearLongTermCapitalGains + currentYearShortTermCapitalGains;
          currentYear.incomeDividend += currentYearIncomeDividends;

          priorYear.capitalGainDistributionLongTerm +=
              priorYearLongTermCapitalGains;
          priorYear.capitalGainDistributionShortTerm +=
              priorYearShortTermCapitalGains;
          priorYear.capitalGainDistributionTotal +=
              priorYearLongTermCapitalGains + priorYearShortTermCapitalGains;
          priorYear.incomeDividend += priorYearIncomeDividends;
        }
      });

      return {
        [CURRENT_YEAR]: currentYear,
        [PRIOR_YEAR]: priorYear
      };
    }
);

export const getYearToDateTableData = createSelector(
  getAccountsById,
  getTaxFilter,
  getYtdTaxInformation,
  (accountsById, taxFilter, ytdTaxInformation) => {
    let filteredYtdTaxInformation = ytdTaxInformation;
    if (taxFilter.selection !== 'all') {
      const accounts = Object.values(accountsById);
      const filteredAccountIds = accounts
        .filter(
          filterFunctionMapping(
            taxFilter.selection,
            productMap,
            taxFilter.accountIds
          )
        )
        .map((account) => account.accountId);
      filteredYtdTaxInformation = filteredYtdTaxInformation.filter((taxInfo) =>
        filteredAccountIds.includes(taxInfo.accountId)
      );
    }
    const tableData = {};

    filteredYtdTaxInformation.forEach((taxInfo) => {
      const { accountId } = taxInfo;
      const account = accountsById[accountId];
      if (account && !isViewOnlyAccount(account)) {
        const { accountName, accountType, ticker, retirementAccount } = account;
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);

        const group = tableData[accountType] || [];

        group.push({
          account: {
            accountName,
            accountId,
            isRetirementAccount: retirementAccount,
            accountWChecksum,
            owners,
            ticker
          },
          [CURRENT_YEAR]: {
            incomeDividends: taxInfo.currentYearIncomeDividends,
            longTermCapitalGains: taxInfo.currentYearLongTermCapitalGains,
            shortTermCapitalGains: taxInfo.currentYearShortTermCapitalGains,
            foreignWithholdings: taxInfo.currentYearForeignWithholding,
            federalIncomeTaxWithholding: taxInfo.currentYearFederalWithholding,
            foreignTaxesPaid: taxInfo.currentYearForeignTaxesPaid
          },
          [PRIOR_YEAR]: {
            incomeDividends: taxInfo.priorYearIncomeDividends,
            longTermCapitalGains: taxInfo.priorYearLongTermCapitalGains,
            shortTermCapitalGains: taxInfo.priorYearShortTermCapitalGains,
            foreignWithholdings: taxInfo.priorYearForeignWithholding,
            federalIncomeTaxWithholding: taxInfo.priorYearFederalWithholding,
            foreignTaxesPaid: taxInfo.priorYearForeignTaxesPaid
          }
        });

        tableData[accountType] = group;
      }
    });

    const ytdTableRows = Object.entries(tableData)
      .map(([accountType, rows]) => ({
        accountType,
        isRetirementAccount: rows[0].account.isRetirementAccount,
        rows: rows.sort((a, b) =>
          a.account.accountName.localeCompare(b.account.accountName)
        )
      }))
      .sort((a, b) => a.accountType.localeCompare(b.accountType));

    return ytdTableRows.map((headerRow) => {
      let currentYearTotals = {
        incomeDividends: 0,
        longTermCapitalGains: 0,
        shortTermCapitalGains: 0,
        foreignWithholdings: 0,
        federalIncomeTaxWithholding: 0,
        foreignTaxesPaid: 0
      };

      let priorYearTotals = {
        incomeDividends: 0,
        longTermCapitalGains: 0,
        shortTermCapitalGains: 0,
        foreignWithholdings: 0,
        federalIncomeTaxWithholding: 0,
        foreignTaxesPaid: 0
      };

      headerRow.rows.forEach((row) => {
        currentYearTotals.incomeDividends += row[CURRENT_YEAR].incomeDividends;
        currentYearTotals.longTermCapitalGains +=
          row[CURRENT_YEAR].longTermCapitalGains;
        currentYearTotals.shortTermCapitalGains +=
          row[CURRENT_YEAR].shortTermCapitalGains;
        currentYearTotals.foreignWithholdings +=
          row[CURRENT_YEAR].foreignWithholdings;
        currentYearTotals.federalIncomeTaxWithholding +=
          row[CURRENT_YEAR].federalIncomeTaxWithholding;
        currentYearTotals.foreignTaxesPaid +=
          row[CURRENT_YEAR].foreignTaxesPaid;

        priorYearTotals.incomeDividends += row[PRIOR_YEAR].incomeDividends;
        priorYearTotals.longTermCapitalGains +=
          row[PRIOR_YEAR].longTermCapitalGains;
        priorYearTotals.shortTermCapitalGains +=
          row[PRIOR_YEAR].shortTermCapitalGains;
        priorYearTotals.foreignWithholdings +=
          row[PRIOR_YEAR].foreignWithholdings;
        priorYearTotals.federalIncomeTaxWithholding +=
          row[PRIOR_YEAR].federalIncomeTaxWithholding;
        priorYearTotals.foreignTaxesPaid += row[PRIOR_YEAR].foreignTaxesPaid;
      });

      return {
        ...headerRow,
        totals: {
          [CURRENT_YEAR]: {
            incomeDividends: currentYearTotals.incomeDividends,
            longTermCapitalGains: currentYearTotals.longTermCapitalGains,
            shortTermCapitalGains: currentYearTotals.shortTermCapitalGains,
            foreignWithholdings: currentYearTotals.foreignWithholdings,
            federalIncomeTaxWithholding:
            currentYearTotals.federalIncomeTaxWithholding,
            foreignTaxesPaid: currentYearTotals.foreignTaxesPaid
          },
          [PRIOR_YEAR]: {
            incomeDividends: priorYearTotals.incomeDividends,
            longTermCapitalGains: priorYearTotals.longTermCapitalGains,
            shortTermCapitalGains: priorYearTotals.shortTermCapitalGains,
            foreignWithholdings: priorYearTotals.foreignWithholdings,
            federalIncomeTaxWithholding:
            priorYearTotals.federalIncomeTaxWithholding,
            foreignTaxesPaid: priorYearTotals.foreignTaxesPaid
          }
        }
      };
    });
  }
);

const sumObjectsByKey = (...objs) => {
  return objs.reduce((acc, curr) => {
    Object.keys(curr || {}).forEach(
      (key) => (acc[key] = (acc[key] || 0) + curr[key])
    );
    return acc;
  }, {});
};

/**
 * This selector gets a list of filters in the following format
 * and aggregate accounts based on what the filter's shouldAggregate method returns
 * @param filters: [{fieldName: '', shouldAggregate: (account) => true}, ..]
 * @returns: object {[filterFieldName]: { rows: [], totals: {} }, ...}
 */
export const getAggregatedYearToDateTableData = (filters) =>
  createSelector(getYearToDateTableData, (tableData) => {
    const initialValue = filters.reduce((acc, { fieldName }) => {
      return {
        ...acc,
        [fieldName]: {
          totals: {},
          rows: []
        }
      };
    }, {});

    return tableData.reduce((acc, currRow) => {
      //check for all the aggregation conditions. There could be repeated items.
      return filters.reduce((acc, { fieldName, shouldAggregate }) => {
        if (!shouldAggregate(currRow)) {
          return acc;
        }
        return {
          ...acc,
          [fieldName]: {
            totals: {
              [PRIOR_YEAR]: sumObjectsByKey(
                acc[fieldName].totals[PRIOR_YEAR],
                currRow.totals[PRIOR_YEAR]
              ),
              [CURRENT_YEAR]: sumObjectsByKey(
                acc[fieldName].totals[CURRENT_YEAR],
                currRow.totals[CURRENT_YEAR]
              )
            },
            rows: [...acc[fieldName].rows, currRow]
          }
        };
      }, acc);
    }, initialValue);
  });

export const getTurboTaxLinkInfo = createSelector(
  getCustomerServiceType,
  (customerServiceType) => {
    let link = taxResourcesTurboTaxLink(customerServiceType);
    let linkText = 'TurboTax® Discounts';

    if (customerServiceType === SERVICE_TYPE_HNW) {
      linkText = 'Discounted TurboTax®';
    }

    if (customerServiceType === SERVICE_TYPE_MIL) {
      linkText = 'Discounted TurboTax®';
    }

    return {
      link,
      linkText
    };
  }
);

export const getAccountCostBasisBalances = createSelector(
  getAccountDetailsId,
  getCostBasisBalances,
  (accountId, costBasisBalances) => {
    const costBasisForAccount = costBasisBalances
      .filter((costBasis) => costBasis.accountId === accountId)
      .sort((a, b) => a.taxYear - b.taxYear);
    return costBasisForAccount[costBasisForAccount.length - 1] || {};
  }
);

export const getAccountsCostBasisTableData = createSelector(
  getAccountDetailsId,
  getAccountsById,
  getCostBasisTableData,
  (accountDetailsId, accountsById, costBasisData) => {
    const { unrealizedCostBasisRows } = costBasisData[CURRENT_YEAR];
    let filteredData = {};
    const account = accountsById[accountDetailsId];

    unrealizedCostBasisRows.length &&
    unrealizedCostBasisRows
      .filter((rowData) => rowData.accountType === account.accountType)
      .forEach((data) => {
        filteredData = {
          accountType: data.accountType,
          rows:
            data.rows.length &&
            data.rows.filter(
              (row) => row.account.taxesUniqueId === accountDetailsId
            )
        };
      });
    return filteredData;
  }
);

export const getCostBasisAsOfDate = createSelector(
  getAccountCostBasisBalances,
  getPriorBusinessDay,
  (costBasis, priorBusinessDay) =>
    costBasis.asOfDate
      ? moment.utc(costBasis.asOfDate).format(DATE_DISPLAY_FORMAT)
      : moment.utc(priorBusinessDay).format(DATE_DISPLAY_FORMAT)
);


export const getCostBasisAsOfDateV2 = createSelector(
  getBalances,
  getAsOfDate,
  getRZGainsLossesCurrentYear,
  getURZGainsLossesData,
  (balances, asOfDate='', RZDcurrentYear, URZcurrentYear) => {
    if (
      !isNil(balances?.asOfDate)
    ) {
      return balances.asOfDate;
    }
    if (
      URZcurrentYear?.length > 0
    ) {
      const firstValidURZ = URZcurrentYear.find((element) => element?.unrealizedCostBasisDetails?.asOfDate)
      if (firstValidURZ) {
        return firstValidURZ?.unrealizedCostBasisDetails?.asOfDate;
      }
    }
    if (
      RZDcurrentYear?.length > 0
    ) {
      const firstValidRZD = RZDcurrentYear.find((element) => element?.realizedCurrentYearCostBasisDetails?.asOfDate)
      if (firstValidRZD) {
        return firstValidRZD?.realizedCurrentYearCostBasisDetails?.asOfDate;
      }
    }
    return asOfDate;
  }
);

export const getAccountCostBasisElection = createSelector(
  getAccountDetailsId,
  getCostBasisElections,
  (accountId, costBasisElections) =>
    costBasisElections.find(
      (account) => account.accountDescriptor.accountId === accountId
    ) || {}
);

export const getIRASummary = createSelector(
  getIraContributions,
  getIraDistributions,
  getIraWithholdings,
  (iraContributions, iraDistributions, iraWithholdings) => {
    const currentYearData = {
      asOfDate: null,
      contributionList: [],
      distributionList: [],
      withholdingList: [],
      totalContributions: 0,
      totalDistributions: 0,
      totalWithholdings: 0
    };

    const priorYearData = {
      asOfDate: null,
      contributionList: [],
      totalContributions: 0
    };

    iraContributions.forEach((contribution) => {
      const { amount, currentYear, date, subTypeName } = contribution;
      const label = subTypeName.replace(' Contribution', '');
      if (currentYear) {
        currentYearData.totalContributions += amount;
        currentYearData.contributionList.push({
          label,
          amount
        });
        if (currentYearData.asOfDate) {
          currentYearData.asOfDate = moment
            .utc(date)
            .isAfter(currentYearData.asOfDate)
            ? date
            : currentYearData.asOfDate;
        } else {
          currentYearData.asOfDate = date;
        }
      } else {
        priorYearData.totalContributions += amount;
        priorYearData.contributionList.push({
          label,
          amount
        });
        if (priorYearData.asOfDate) {
          priorYearData.asOfDate = moment
            .utc(date)
            .isAfter(priorYearData.asOfDate)
            ? date
            : priorYearData.asOfDate;
        } else {
          priorYearData.asOfDate = date;
        }
      }
    });

    iraDistributions.forEach((distribution) => {
      const { amount, currentYear, date, subTypeName } = distribution;
      const label = subTypeName.replace(' Distribution', '');
      if (currentYear) {
        currentYearData.totalDistributions += amount;
        currentYearData.distributionList.push({
          label,
          amount
        });
        if (currentYearData.asOfDate) {
          currentYearData.asOfDate = moment
            .utc(date)
            .isAfter(currentYearData.asOfDate)
            ? date
            : currentYearData.asOfDate;
        } else {
          currentYearData.asOfDate = date;
        }
      }
    });

    iraWithholdings.forEach((withholding) => {
      const { amount, currentYear, date, subTypeName } = withholding;
      const label = subTypeName.replace(' Withholdings', '');
      if (currentYear) {
        currentYearData.totalWithholdings += amount;
        currentYearData.withholdingList.push({
          label,
          amount
        });
        if (currentYearData.asOfDate) {
          currentYearData.asOfDate = moment
            .utc(date)
            .isAfter(currentYearData.asOfDate)
            ? date
            : currentYearData.asOfDate;
        } else {
          currentYearData.asOfDate = date;
        }
      }
    });

    currentYearData.contributionList.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    currentYearData.distributionList.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    currentYearData.withholdingList.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    priorYearData.contributionList.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    if (currentYearData.asOfDate) {
      currentYearData.asOfDate = moment
        .utc(currentYearData.asOfDate)
        .format(DATE_DISPLAY_FORMAT);
    }
    if (priorYearData.asOfDate) {
      priorYearData.asOfDate = moment
        .utc(priorYearData.asOfDate)
        .format(DATE_DISPLAY_FORMAT);
    }

    return {
      [CURRENT_YEAR]: currentYearData,
      [PRIOR_YEAR]: priorYearData
    };
  }
);

export const getCollegeSavingsTableData = createSelector(
  getAccountsById,
  getCollegeSavingsActivities,
  getCollegeSavingsFilter,
  (accountsById, collegeSavingsActivities, collegeSavingsFilter) => {
    let filteredCollegeSavingsActivities = collegeSavingsActivities;

    if (collegeSavingsFilter.selection !== 'all') {
      const accounts = Object.values(accountsById);
      const filteredAccountIds = accounts
        .filter(
          filterFunctionMapping(
            collegeSavingsFilter.selection,
            productMap,
            collegeSavingsFilter.accountIds
          )
        )
        .map((account) => account.accountId);
      filteredCollegeSavingsActivities =
        filteredCollegeSavingsActivities.filter((collegeSavings) =>
          filteredAccountIds.includes(collegeSavings.accountId)
        );
    }
    const tableData = {};

    filteredCollegeSavingsActivities.forEach((collegeSavings) => {
      const { accountId } = collegeSavings;
      const account = accountsById[accountId];
      if (account && !isViewOnlyAccount(account)) {
        const { accountName, accountType, balanceValue, productType } = account;
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);

        const groupName = `${accountType}:${owners}`;

        const group = tableData[groupName] || [];

        group.push({
          account: {
            accountName,
            accountId,
            accountWChecksum,
            productType
          },
          accountOwner: owners,
          contributions: collegeSavings.contributionsAmount,
          distributions: collegeSavings.distributionsAmount,
          balance: balanceValue
        });

        tableData[groupName] = group;
      }
    });

    const collegeSavingsTableRows = Object.entries(tableData)
      .map(([accountTypeWithOwners, rows]) => {
        const [accountType, accountOwner] = accountTypeWithOwners.split(':');

        return {
          accountType,
          accountOwner,
          contributionSum: rows.reduce(
            (acc, row) => row.contributions + acc,
            0
          ),
          distributionSum: rows.reduce(
            (acc, row) => row.distributions + acc,
            0
          ),
          balanceSum: rows.reduce((acc, row) => row.balance + acc, 0),
          rows: rows.sort((a, b) =>
            a.account.accountName.localeCompare(b.account.accountName)
          )
        };
      })
      .sort((a, b) => a.accountType.localeCompare(b.accountType));

    return collegeSavingsTableRows;
  }
);

export const getModifiedCostBasisElectionsPayload = createSelector(
  getCostBasisElections,
  getPendingCostBasisElections,
  (costBasisElections, pendingCostBasisElections) =>
    generateCostBasisPayload(costBasisElections, pendingCostBasisElections)
);

export const shouldDisableCostBasisMethodSave = createSelector(
  getPendingCostBasisElections,
  getModifiedCostBasisElectionsPayload,
  (pendingCostBasisElections, { modifiedCostBasisElections }) => {
    return (
      modifiedCostBasisElections.length === 0 ||
      pendingCostBasisElections.some(
        (costBasis) => costBasis.costBasisTypeId === SPECIFIC_LOT_IDENTIFICATION
      )
    );
  }
);

export const getCostBasisMethodTableData = createSelector(
  getAccountsById,
  getPendingTransactionsTable,
  getPendingCostBasisElections,
  (accountsById, pendingTransactionsTable, costBasisElections) => {
    if (isEmpty(accountsById)) {
      return [];
    }
    const accountTypeTableData = {};

    costBasisElections.forEach((costBasisElection) => {
      const { accountDescriptor } = costBasisElection;
      const account = accountsById[accountDescriptor.accountId];

      if (account) {
        const {
          accountId,
          accountName,
          accountType,
          retirementAccount,
          productType,
          ticker
        } = account;
        const tableData = accountTypeTableData[accountType] || [];
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);
        // TODO: HAS LOGIC NEEDED
        const hasPendingTransaction =
          filterPendingTransactionsByAccount(account, pendingTransactionsTable)
            .length > 0;
        tableData.push({
          ...costBasisElection,
          account: {
            accountId,
            accountName,
            accountType,
            accountWChecksum,
            owners,
            productType,
            ticker,
            hasPendingTransaction,
            retirementAccount
          }
        });
        accountTypeTableData[accountType] = tableData;
      }
    });

    const tableRows = Object.entries(accountTypeTableData)
      .map(([accountType, rows]) => ({
        accountType,
        rows: sortRows(rows),
        isRetirement: rows[0].account.retirementAccount === true
      }))
      .sort((a, b) => a.accountType.localeCompare(b.accountType));

    return tableRows;
  }
);

const sortRows = (rows) => {
  return rows.sort((a, b) => {
    return (
      a.account.accountName.localeCompare(b.account.accountName) ||
      a.account.accountWChecksum.localeCompare(b.account.accountWChecksum) ||
      a.account.ticker.localeCompare(b.account.ticker)
    );
  });
};

export const hasRMDEligibleAccounts = createSelector(
  getAccounts,
  (accounts) => {
    return accounts.some(
      (account) =>
        isRMDEligibleAccount(account.accountTypeId) && account.availableBalance
    );
  }
);

function createAccountList(
  account,
  accountsByType,
  accountNameMod,
  accountType,
  election,
  fundMethodType,
  fundMethodTypeId,
  numberWithChecksum,
  owners,
  pendingIndicator,
  hasPendingTransaction,
  isBrokerageStockReinvestment,
  isBrokerageMutualFund
) {
  const costBasisMethodMod =
    fundMethodType.indexOf('(') > 0
      ? fundMethodType.substring(0, fundMethodType.indexOf('('))
      : fundMethodType;
  const costBasisTypeIdMod = fundMethodTypeId;

  const accountData = {
    ...account,
    ...election,
    accountNameMod,
    costBasisMethodMod,
    costBasisTypeIdMod,
    numberWithChecksum,
    pendingIndicator,
    hasPendingTransaction,
    isBrokerageStockReinvestment,
    isBrokerageMutualFund
  };

  const accountList = accountsByType[accountType + ' (' + owners + ')'] || [];
  accountList.push(accountData);
  return accountList;
}

export function createRegistrationTable(accountsById, costBasisElections, pendingTransactionsTable) {
  const accountsByType = {};
  costBasisElections.forEach((election) => {
    const {
      accountDescriptor,
      costBasisMethod,
      costBasisTypeId,
      pendingIndicator,
      isBrokerage
    } = election;
    const account = accountsById[accountDescriptor.accountId];
    if (account) {
      const { accountChecksum, accountNumber, accountName, accountType, isViewOnly, retirementAccount, closedAccount } =
        account;
      const owners = extractAndFormatOwners(account);
      const numberWithChecksum = `${accountNumber}-${accountChecksum}`;

      const hasPendingTransaction =
        !isBrokerage && (filterPendingTransactionsByAccount(account, pendingTransactionsTable)
          .length > 0);

      let accountNameMod;
      let fundMethodType;
      let fundMethodTypeId;
      let accountList;
      let isBrokerageStockReinvestment;
      let isBrokerageMutualFund;
      if (!isViewOnly && !retirementAccount && !(isBrokerage && closedAccount)) {
        if (isBrokerage) {
          const {
            brokerageCostBasisMethods: {
              mutualFundCostBasisMethod,
              mutualFundCostBasisTypeId,
              otherSecurityCostBasisMethod,
              otherSecurityCostBasisTypeId,
              dripCostBasisMethod,
              dripCostBasisTypeId
            }
          } = election;

          accountNameMod = accountName + ' - Mutual Funds';
          const mutualFundCostBasisMethodOptions =
            brokerageCostBasisElections.filter(
              (opt) =>
                opt.value === mutualFundCostBasisTypeId &&
                opt.includedForBrokerageMutualFund
            );
          fundMethodType =
            mutualFundCostBasisMethod === null
              ? ''
              : mutualFundCostBasisMethodOptions.length > 0
                ? mutualFundCostBasisMethodOptions[0].label
                : mutualFundCostBasisMethod;
          fundMethodTypeId = mutualFundCostBasisTypeId;
          isBrokerageStockReinvestment = false;
          isBrokerageMutualFund = true;
          accountList = createAccountList(
            account,
            accountsByType,
            accountNameMod,
            accountType,
            election,
            fundMethodType,
            fundMethodTypeId,
            numberWithChecksum,
            owners,
            pendingIndicator,
            hasPendingTransaction,
            isBrokerageStockReinvestment,
            isBrokerageMutualFund
          );
          accountsByType[accountType + ' (' + owners + ')'] = accountList;

          accountNameMod = accountName + ' - All Other Securities';
          const otherSecurityCostBasisMethodOptions =
            brokerageCostBasisElections.filter(
              (opt) =>
                opt.value === otherSecurityCostBasisTypeId &&
                opt.includedForBrokerageotherSecurities
            );
          fundMethodType =
            otherSecurityCostBasisMethod === null
              ? ''
              : otherSecurityCostBasisMethodOptions.length > 0
                ? otherSecurityCostBasisMethodOptions[0].label
                : otherSecurityCostBasisMethod;

          fundMethodTypeId = otherSecurityCostBasisTypeId || 1;
          isBrokerageStockReinvestment = false;
          isBrokerageMutualFund = false;
          accountList = createAccountList(
            account,
            accountsByType,
            accountNameMod,
            accountType,
            election,
            fundMethodType,
            fundMethodTypeId,
            numberWithChecksum,
            owners,
            pendingIndicator,
            hasPendingTransaction,
            isBrokerageStockReinvestment,
            isBrokerageMutualFund
          );
          accountsByType[accountType + ' (' + owners + ')'] = accountList;

          accountNameMod =
            accountName + ' - Stocks in Dividend Reinvestment Plans';

          const dripCostBasisMethodOptions = brokerageCostBasisElections.filter(
            (opt) =>
              opt.value === dripCostBasisTypeId &&
              opt.includedForBrokerageStockInvestment
          );
          fundMethodType =
            dripCostBasisMethod === null
              ? ''
              : dripCostBasisMethodOptions.length > 0
                ? dripCostBasisMethodOptions[0].label
                : dripCostBasisMethod;

          fundMethodTypeId = dripCostBasisTypeId || 1;
          isBrokerageStockReinvestment = true;
          isBrokerageMutualFund = false;
          accountList = createAccountList(
            account,
            accountsByType,
            accountNameMod,
            accountType,
            election,
            fundMethodType,
            fundMethodTypeId,
            numberWithChecksum,
            owners,
            pendingIndicator,
            hasPendingTransaction,
            isBrokerageStockReinvestment,
            isBrokerageMutualFund
          );
          accountsByType[accountType + ' (' + owners + ')'] = accountList;
        } else {
          accountNameMod = accountName;
          fundMethodType = costBasisMethod;
          fundMethodTypeId = costBasisTypeId;
          isBrokerageStockReinvestment = false;
          isBrokerageMutualFund = false;
          accountList = createAccountList(
            account,
            accountsByType,
            accountNameMod,
            accountType,
            election,
            fundMethodType,
            fundMethodTypeId,
            numberWithChecksum,
            owners,
            pendingIndicator,
            hasPendingTransaction,
            isBrokerageStockReinvestment,
            isBrokerageMutualFund
          );
          accountsByType[accountType + ' (' + owners + ')'] = accountList;
        }
      }
    }
  });

  for (const accountType in accountsByType) {
    accountsByType[accountType].sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }

  return accountsByType;
}

export const getCostBasisRegistrationTable = createSelector(
  getAccountsById,
  getCostBasisElections,
  getPendingTransactionsTable,
  (accountsById, costBasisElections, pendingTransactionsTable) => {
    return createRegistrationTable(accountsById, costBasisElections, pendingTransactionsTable);
  }
);
